import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import AndroidIcon from '@mui/icons-material/Android';
import InstallMobileIcon from '@mui/icons-material/InstallMobile';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { ReactComponent as AppleIcon } from '../../assets/icons/apple.svg';
import BaseActionButton from '../../components/designe/ActionButtons/BaseActionButton';
import DownloadActionButton from '../../components/designe/ActionButtons/DownloadActionButton';
import PageContainer from '../../components/layout/page-container/PageContainer';
import { useRegisterPageContainerError } from '../../components/layout/page-container/page-container-error-handler';
import { usePwaInstall } from '../../hooks/use-pwa-install';
import { useGetAppVariables } from '../../redux/api/appConfigApi';
import {
  AppVariableKeys,
  getAppVariableValueInsensitive,
} from '../../types/interfaces/app-variable';
import { isValidHttpUrl } from '../../utils/platform/is-valid-http-url';
import { openExternalUrl } from '../../utils/platform/open-external-url';
import { useStyles } from './Resources.styles';

function Resources(props) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  const { classes } = useStyles(theme);
  const {
    data: variables,
    refetch,
    isLoading,
    isFetching,
    isError,
    error,
  } = useGetAppVariables();
  const apkLink = getAppVariableValueInsensitive(
    variables,
    AppVariableKeys.appStorageLink,
  );
  const iosBuildLink = getAppVariableValueInsensitive(
    variables,
    AppVariableKeys.iosBuildLink,
  );
  const version = getAppVariableValueInsensitive(
    variables,
    AppVariableKeys.version,
  );
  const apkFileName =
    getAppVariableValueInsensitive(variables, AppVariableKeys.apkFileName) ??
    'SportGround.apk';
  const hasValidIosBuildLink = isValidHttpUrl(iosBuildLink);
  const { canInstallPwa, isPwaInstalled, promptInstallPwa, isIosBrowser } =
    usePwaInstall();
  const pwaHintKey = canInstallPwa
    ? 'resources.pwaInstallHint'
    : isIosBrowser
      ? 'resources.pwaInstallHintIos'
      : null;
  const handleRetry = useCallback(() => {
    void refetch();
  }, [refetch]);

  useRegisterPageContainerError({
    errors: isError && error ? [error] : [],
    isLoading: !variables && (isLoading || isFetching),
    hasData: Boolean(variables?.length),
    onRetry: handleRetry,
  });

  return (
    <PageContainer
      title={t('resources.resources')}
      onRefresh={() => void refetch()}>
      <Box className={classes.resourcesWrapper}>
        {apkLink && (
          <Box className={classes.resourcesItem}>
            <AndroidIcon />
            <Typography variant={'text-lg'} flex={1}>
              {t('resources.apkLabel')}
              {version}
            </Typography>
            <DownloadActionButton link={apkLink} fileName={apkFileName} />
          </Box>
        )}
        {hasValidIosBuildLink && (
          <Box className={classes.resourcesItem}>
            <AppleIcon className={classes.resourceIcon} />
            <Typography variant={'text-lg'} flex={1}>
              {t('resources.iosLabel')}
              {version}
            </Typography>
            <BaseActionButton
              onClick={() => void openExternalUrl(iosBuildLink)}>
              <OpenInNewIcon />
            </BaseActionButton>
          </Box>
        )}
        {!isPwaInstalled && (canInstallPwa || isIosBrowser) && (
          <Box className={classes.resourcesItem}>
            <InstallMobileIcon />
            <Box flex={1} display="flex" flexDirection="column" gap={0.5}>
              <Typography variant={'text-lg'}>
                {t('resources.pwaLabel')}
              </Typography>
              {pwaHintKey && (
                <Typography variant={'text-sm'} color="text.secondary">
                  {t(pwaHintKey)}
                </Typography>
              )}
            </Box>
            {canInstallPwa && (
              <BaseActionButton onClick={() => void promptInstallPwa()}>
                <OpenInNewIcon />
              </BaseActionButton>
            )}
          </Box>
        )}
      </Box>
    </PageContainer>
  );
}

export default Resources;
